import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, LayoutGrid, Plus, Share2, Table2 } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { PortalDsButton } from 'src/components/portal-design-system/portal-ds-button';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import {
  useDeletePacingBudgetMutation,
  useGetPacingBoardQuery,
  useGetPacingBoardsQuery,
} from '../endpoints/pacing-board.endpoints';
import PacingBudgetRail from '../components/pacing-board/pacing-budget-rail';
import PacingBudgetHero from '../components/pacing-board/pacing-budget-hero';
import PacingAlertsPanel from '../components/pacing-board/pacing-alerts-panel';
import PacingBoardCreateModal from '../components/pacing-board/pacing-board-create-modal';
import PacingBoardShareModal from '../components/pacing-board/pacing-board-share-modal';
import PacingBudgetModal from '../components/pacing-board/pacing-budget-modal';
import PacingCampaignsTable from '../components/pacing-board/pacing-campaigns-table';

type DashboardTab = 'budgets' | 'alerts';
type BudgetViewMode = 'board' | 'table';

const PacingTab: React.FC = () => {
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DashboardTab>('budgets');
  const [budgetViewMode, setBudgetViewMode] = useState<BudgetViewMode>('board');
  const [boardMenuOpen, setBoardMenuOpen] = useState(false);
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const [showShareBoard, setShowShareBoard] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState<string | null>(null);

  const { data: boards = [], isLoading: boardsLoading } = useGetPacingBoardsQuery();
  const {
    data: boardData,
    isLoading: boardLoading,
    isFetching: boardFetching,
  } = useGetPacingBoardQuery(selectedBoardId ?? '', { skip: !selectedBoardId });
  const [deleteBudget] = useDeletePacingBudgetMutation();

  useEffect(() => {
    if (!selectedBoardId && boards.length > 0) {
      setSelectedBoardId(boards[0].id);
    }
  }, [boards, selectedBoardId]);

  const budgets = boardData?.budgets ?? [];
  const selectedBudget = useMemo(
    () => budgets.find((b) => b.id === selectedBudgetId) ?? budgets[0] ?? null,
    [budgets, selectedBudgetId],
  );

  useEffect(() => {
    if (budgets.length > 0 && !selectedBudgetId) {
      setSelectedBudgetId(budgets[0].id);
    }
  }, [budgets, selectedBudgetId]);

  const canEdit = boardData?.board.is_owner || boardData?.board.role === 'EDIT';
  const alertCount = boardData?.alerts?.length ?? 0;

  const handleSelectBudget = (budgetId: string) => {
    setSelectedBudgetId(budgetId);
    setActiveTab('budgets');
  };

  const handleDeleteBudget = async () => {
    if (!selectedBudget) return;
    if (!window.confirm(`Delete budget "${selectedBudget.campaign_name}"?`)) return;
    await deleteBudget(selectedBudget.id).unwrap();
    setSelectedBudgetId(null);
  };

  const handleEditBudget = (budgetId?: string) => {
    setEditingBudget(budgetId ?? selectedBudget?.id ?? null);
    setShowBudgetModal(true);
  };

  const handleAddBudget = () => {
    setEditingBudget(null);
    setShowBudgetModal(true);
  };

  if (boardsLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[#9CA3AF]">
        Loading pacing boards…
      </div>
    );
  }

  if (boards.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-[#F4F5F8]">
        <p className="text-sm text-[#5E6278]">Create a pacing board to start tracking budgets.</p>
        <PortalDsButton variant="accent" onClick={() => setShowCreateBoard(true)}>
          <Plus className="h-4 w-4" />
          Create board
        </PortalDsButton>
        <PacingBoardCreateModal
          open={showCreateBoard}
          onClose={() => setShowCreateBoard(false)}
          onCreated={(id) => setSelectedBoardId(id)}
        />
      </div>
    );
  }

  const currentBoard = boards.find((b) => b.id === selectedBoardId);

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-[#EDEEF2] bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              type="button"
              onClick={() => setBoardMenuOpen(!boardMenuOpen)}
              className="flex items-center gap-2 rounded-lg border border-[#D9DBE3] bg-white px-3 py-2 text-sm font-semibold text-[#0D0F1A] hover:bg-[#F0F1F5]"
            >
              {currentBoard?.name ?? 'Select board'}
              <ChevronDown className="h-4 w-4 text-[#9CA3AF]" />
            </button>
            {boardMenuOpen && (
              <div className="absolute left-0 top-full z-20 mt-1 min-w-[220px] rounded-lg border border-[#EDEEF2] bg-white py-1 shadow-lg">
                {boards.map((board) => (
                  <button
                    key={board.id}
                    type="button"
                    onClick={() => {
                      setSelectedBoardId(board.id);
                      setSelectedBudgetId(null);
                      setBoardMenuOpen(false);
                    }}
                    className={cn(
                      'w-full px-4 py-2 text-left text-sm hover:bg-[#F0F1F5]',
                      board.id === selectedBoardId && 'bg-[#EFF6FF] font-semibold',
                    )}
                  >
                    {board.name}
                    <span className="ml-2 text-xs text-[#9CA3AF]">({board.budget_count})</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <PortalDsButton variant="ghost" size="sm" onClick={() => setShowCreateBoard(true)}>
            <Plus className="h-4 w-4" />
            New board
          </PortalDsButton>
        </div>

        <div className="flex items-center gap-2">
          {activeTab === 'budgets' && (
            <div
              className={portalDs.segmentGroup}
              role="group"
              aria-label="Budget view mode"
            >
              <button
                type="button"
                className={cn(
                  portalDs.segment,
                  'inline-flex items-center gap-1.5',
                  budgetViewMode === 'board' && portalDs.segmentActive,
                )}
                onClick={() => setBudgetViewMode('board')}
                aria-pressed={budgetViewMode === 'board'}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Board
              </button>
              <button
                type="button"
                className={cn(
                  portalDs.segment,
                  'inline-flex items-center gap-1.5',
                  budgetViewMode === 'table' && portalDs.segmentActive,
                )}
                onClick={() => setBudgetViewMode('table')}
                aria-pressed={budgetViewMode === 'table'}
              >
                <Table2 className="h-3.5 w-3.5" />
                Table
              </button>
            </div>
          )}

          {canEdit && (
            <PortalDsButton variant="secondary" size="sm" onClick={() => setShowShareBoard(true)}>
              <Share2 className="h-4 w-4" />
              Share
            </PortalDsButton>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[#EDEEF2] bg-white px-4">
        <TabButton
          active={activeTab === 'budgets'}
          onClick={() => setActiveTab('budgets')}
          label="Budgets"
        />
        <TabButton
          active={activeTab === 'alerts'}
          onClick={() => setActiveTab('alerts')}
          label="Alerts"
          badge={alertCount > 0 ? alertCount : undefined}
        />
      </div>

      {/* Content */}
      <div className="flex flex-1 overflow-hidden">
        {boardLoading && !boardData ? (
          <div className="flex flex-1 overflow-hidden">
            <PacingBudgetRail
              budgets={[]}
              selectedBudgetId={null}
              onSelect={() => undefined}
              canEdit={false}
              onAddBudget={handleAddBudget}
              isRefreshing
            />
            <div className="flex flex-1 items-center justify-center text-sm text-[#9CA3AF]">
              Loading board data…
            </div>
          </div>
        ) : activeTab === 'alerts' ? (
          <PacingAlertsPanel
            alerts={boardData?.alerts ?? []}
            onSelectBudget={handleSelectBudget}
          />
        ) : budgetViewMode === 'table' ? (
          <PacingCampaignsTable
            budgets={budgets}
            canEdit={canEdit}
            isRefreshing={boardFetching}
            onEdit={handleEditBudget}
            onAddBudget={handleAddBudget}
          />
        ) : (
          <>
            <PacingBudgetRail
              budgets={budgets}
              selectedBudgetId={selectedBudget?.id ?? null}
              onSelect={setSelectedBudgetId}
              canEdit={canEdit}
              onAddBudget={handleAddBudget}
              isRefreshing={boardFetching}
            />
            <PacingBudgetHero
              budget={selectedBudget}
              canEdit={canEdit}
              onEdit={() => handleEditBudget()}
              onDelete={handleDeleteBudget}
            />
          </>
        )}
      </div>

      <PacingBoardCreateModal
        open={showCreateBoard}
        onClose={() => setShowCreateBoard(false)}
        onCreated={(id) => setSelectedBoardId(id)}
      />

      {selectedBoardId && (
        <PacingBoardShareModal
          open={showShareBoard}
          onClose={() => setShowShareBoard(false)}
          boardId={selectedBoardId}
          shares={boardData?.shares ?? []}
        />
      )}

      {selectedBoardId && (
        <PacingBudgetModal
          open={showBudgetModal}
          onClose={() => {
            setShowBudgetModal(false);
            setEditingBudget(null);
          }}
          boardId={selectedBoardId}
          budget={editingBudget ? budgets.find((b) => b.id === editingBudget) : null}
          onSaved={(budgetId) => {
            setSelectedBudgetId(budgetId);
            setActiveTab('budgets');
          }}
        />
      )}
    </div>
  );
};

function TabButton({
  active,
  onClick,
  label,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  badge?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative px-4 py-2.5 text-sm font-semibold transition-colors',
        active
          ? 'text-[#2563EB] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#2563EB]'
          : 'text-[#5E6278] hover:text-[#0D0F1A]',
      )}
    >
      {label}
      {badge != null && (
        <span className="ml-1.5 rounded-full bg-[#DC2626] px-1.5 py-0.5 text-[10px] font-bold text-white">
          {badge}
        </span>
      )}
    </button>
  );
}

export default PacingTab;
